import { useCallback, useEffect, useState } from "react";

import {
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

import { Alert } from "../base/Alert";
import Card from "../shared/Card/Card";
import { Button } from "../base/Button";
import MiniTable from "../base/MiniTable";
import LoadingWheel from "../shared/LoadingWheel";
import { errorResolver } from "../../utils/errorResolver";
import useShopifyStores from "../../data/ShopifyStore/useShopifyStores";
import { IShopifyStore } from "../../models/ShopifyStore/ShopifyStore";
import { CreateUpdateShopifyStoreModal } from "./CreateUpdateShopifyStoreModal";
import useShopifyStoreMutations from "../../data/ShopifyStore/useShopifyStoreMutations";

interface CustomerShopifyStoreListProps {
  customerId: string;
}

export function CustomerShopifyStoreList({
  customerId,
}: CustomerShopifyStoreListProps) {
  const { shopifyStores } = useShopifyStores(customerId);
  const { remove } = useShopifyStoreMutations(customerId);

  const [pageNumber, setPageNumber] = useState(0);
  const [itemsPerPage] = useState(10);
  const [numPages, setNumPages] = useState(0);
  const [stores, setStores] = useState<IShopifyStore[]>([]);
  const [loading, setLoading] = useState(true);

  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [editStore, setEditStore] = useState<IShopifyStore>();

  const setTableData = useCallback(
    (pageNo: number, perPage: number) => {
      if (shopifyStores.data === null || shopifyStores.data === undefined) return;

      setLoading(true);

      const startIndex = pageNo * perPage;
      const endIndex = startIndex + perPage;

      setStores(shopifyStores.data.slice(startIndex, endIndex));
      setLoading(false);
    },
    [shopifyStores.data]
  );

  const handleNextPage = () => {
    const newPage = pageNumber + 1;

    if (newPage > numPages) return;

    setPageNumber(newPage);
    setTableData(newPage, itemsPerPage);
  };

  const handlePrevPage = () => {
    const newPage = pageNumber - 1;

    if (newPage < 0) return;

    setPageNumber(newPage);
    setTableData(newPage, itemsPerPage);
  };

  const handleCreateUpdateClose = () => {
    setStoreModalOpen(false);
    editStore &&
      setTimeout(() => {
        setEditStore(undefined);
      }, 250);
  };

  const handleDeleteStore = (store: IShopifyStore) => {
    const confirmed = window.confirm(`Delete Shopify store "${store.domain}"?`);

    if (!confirmed) return;

    remove.mutate(store.id);
  };

  useEffect(() => {
    if (
      !shopifyStores.isLoading &&
      shopifyStores.data &&
      shopifyStores.data.length > 0
    ) {
      setNumPages(Math.ceil(shopifyStores.data.length / itemsPerPage));
      setTableData(pageNumber, itemsPerPage);
    } else if (!shopifyStores.isLoading && shopifyStores.data) {
      setStores([]);
      setLoading(false);
    }
  }, [
    shopifyStores.isLoading,
    shopifyStores.data,
    itemsPerPage,
    pageNumber,
    setTableData,
  ]);

  return (
    <>
      <CreateUpdateShopifyStoreModal
        open={storeModalOpen}
        setOpen={handleCreateUpdateClose}
        customerId={customerId}
        store={editStore}
      />
      <Card
        title="Shopify Stores"
        bodyClassName="p-4"
        titleRightContent={
          <Button
            onClick={() => setStoreModalOpen(true)}
            size="sm"
            className="my-[1px] flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5 -ml-1" />
            <p>Add Store</p>
          </Button>
        }
      >
        {shopifyStores.isLoading || loading ? (
          <div className="py-4">
            <LoadingWheel />
          </div>
        ) : shopifyStores.isError ? (
          <div className="p-4">
            <Alert
              severity="error"
              title="Error getting shopify stores"
              message={errorResolver(shopifyStores.error)}
            />
          </div>
        ) : (
          <>
            <MiniTable>
              <MiniTable.Header>
                <MiniTable.HeaderCell>Domain</MiniTable.HeaderCell>
                <MiniTable.HeaderCell>Client Id</MiniTable.HeaderCell>
                <MiniTable.HeaderCell>Publication Id</MiniTable.HeaderCell>
                <MiniTable.HeaderCell>State</MiniTable.HeaderCell>
                <MiniTable.HeaderCell className="text-center">
                  Actions
                </MiniTable.HeaderCell>
              </MiniTable.Header>
              <MiniTable.Body>
                {stores.length > 0 ? (
                  stores.map((store, i) => (
                    <MiniTable.Row key={i}>
                      <MiniTable.Cell>{store.domain || "-"}</MiniTable.Cell>
                      <MiniTable.Cell>{store.appClientId || "-"}</MiniTable.Cell>
                      <MiniTable.Cell>{store.publicationId || "-"}</MiniTable.Cell>
                      <MiniTable.Cell>{store.state || "-"}</MiniTable.Cell>
                      <MiniTable.Cell className="flex items-center justify-center space-x-4">
                        <PencilIcon
                          onClick={() => {
                            setEditStore(store);
                            setStoreModalOpen(true);
                          }}
                          className="h-5 w-5 hover:cursor-pointer"
                        />
                        <TrashIcon
                          onClick={() => handleDeleteStore(store)}
                          className="h-5 w-5 hover:cursor-pointer text-red-600"
                        />
                      </MiniTable.Cell>
                    </MiniTable.Row>
                  ))
                ) : (
                  <MiniTable.Row>
                    <MiniTable.Cell colSpan={5} className="py-4 text-center">
                      No Shopify stores
                    </MiniTable.Cell>
                  </MiniTable.Row>
                )}
              </MiniTable.Body>
            </MiniTable>
            <MiniTable.Pagination
              totalRows={shopifyStores.data?.length ?? 0}
              totalPages={numPages}
              currentPage={pageNumber + 1}
              itemsPerPage={itemsPerPage}
              onPrevClick={handlePrevPage}
              onNextClick={handleNextPage}
            />
          </>
        )}
      </Card>
    </>
  );
}
